/**
 * DIDComm v2 Protocol Parser
 *
 * Parses W3C DIDComm v2 message format.
 * Supports JWE (encrypted) and plaintext messages.
 *
 * Full encryption/decryption handled by dedicated hooks.
 * This parser only handles envelope structure.
 */

import type { IProtocolParser, ParsedMessage } from '../types';
import { createGuid } from '../utils/idGenerator';

interface DIDCommMessage {
  id: string;
  type: string;
  from?: string;
  to?: string[];
  created_time?: number;
  expires_time?: number;
  body: Record<string, unknown>;
  attachments?: Array<Record<string, unknown>>;
}

export class DIDCommV2Parser implements IProtocolParser {
  readonly name = 'didcomm-v2';

  canParse(raw: string | ArrayBuffer): boolean {
    try {
      const text = typeof raw === 'string' ? raw : new TextDecoder().decode(new Uint8Array(raw));
      const trimmed = text.trim();

      // Check for JWE format (encrypted): 5 parts separated by dots
      if (trimmed.split('.').length === 5) {
        return true;
      }

      // Check for JSON format
      if (trimmed.startsWith('{')) {
        const obj = JSON.parse(text) as Record<string, unknown>;
        return typeof obj.type === 'string' && typeof obj.id === 'string';
      }

      return false;
    } catch {
      return false;
    }
  }

  parse(raw: string | ArrayBuffer): ParsedMessage {
    try {
      const text = typeof raw === 'string' ? raw : new TextDecoder().decode(new Uint8Array(raw));

      // If JWE format (encrypted), return as-is for DecryptHook to handle
      if (text.split('.').length === 5) {
        return {
          body: { encrypted: true, jwe: text },
          format: 'didcomm-v2',
        };
      }

      // Otherwise parse as JSON
      const message = JSON.parse(text) as DIDCommMessage;

      // Validate required fields
      if (!message.id || !message.type) {
        return {
          body: null,
          format: 'didcomm-v2',
          error: 'Missing required DIDComm v2 fields (id, type)',
        };
      }

      return {
        body: message,
        format: 'didcomm-v2',
      };
    } catch (error) {
      return {
        body: null,
        format: 'didcomm-v2',
        error: `DIDComm v2 parse error: ${error instanceof Error ? error.message : String(error)}`,
      };
    }
  }

  serialize(body: unknown): string {
    const message = body as Partial<DIDCommMessage>;
    const didcommMsg: DIDCommMessage = {
      id: message.id || createGuid(),
      type: message.type || 'com.example.unknown',
      created_time: message.created_time || Math.floor(Date.now() / 1000),
      body: message.body || {},
      ...message,
    };
    return JSON.stringify(didcommMsg);
  }
}
